"use client"

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { toolsService } from '@edemics/services/toolsService'
import { useAuth } from './auth-provider'
import type { PermissionRule, PermissionAction } from '@edemics/types/permissions'

interface PermissionContextType {
  permissions: PermissionRule[]
  loading: boolean
  hasPermission: (documentType: string, action: PermissionAction | string) => boolean
  getRestriction: (documentType: string) => string
  refreshPermissions: () => void
}

// Default context — fail-CLOSED. Consumers outside a real PermissionProvider
// (test utilities, orphaned trees) get deny-by-default, not the pre-2026-08-23
// allow-by-default that made the whole client-side RBAC layer advisory.
const PermissionContext = createContext<PermissionContextType>({
  permissions: [],
  loading: true,
  hasPermission: () => false,
  getRestriction: () => 'CREATOR',
  refreshPermissions: () => {},
})

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth()
  const [permissions, setPermissions] = useState<PermissionRule[]>([])
  const [loading, setLoading] = useState(true)

  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setPermissions([])
      setLoading(false)
      return
    }

    // Platform users (super-admin, platform staff) bypass tenant RBAC
    // entirely. PF-2 authoritative signal is `kind === 'platform'`, not
    // the role string. Skipping the fetch also avoids a 403 from the BE
    // audience interceptor which rejects platform hits on /tools/*.
    if (user.kind === 'platform') {
      setPermissions([])
      setLoading(false)
      return
    }

    // ACC-BUGS V5 (2026-09-14) — tenant admins also bypass RBAC per
    // policy (see rbac_policy_2026_08_27). The BE PermissionGuard
    // already lets them through server-side; matching that on the FE
    // means the "New Company" / "New Currency" / etc. buttons are
    // actually rendered for the tenant admin. Without this bypass the
    // rules fetch returned an empty ruleset (admins don't need
    // explicit RoleProfile grants) and hasPermission() fail-closed
    // hid every gated action.
    if (user.kind === 'tenant' && user.role === 'admin') {
      setPermissions([])
      setLoading(false)
      return
    }

    try {
      const rules = await toolsService.getMyPermissions()
      setPermissions(rules)
    } catch {
      // Fail-CLOSED: if permissions fail to load we treat the user as having
      // NO rules. Combined with hasPermission's no-rule-means-deny below,
      // this hides gated UI until the fetch succeeds. Previously we defaulted
      // to allow-all under the label "graceful degradation" — that hid the
      // failure but silently unlocked the entire app.
      setPermissions([])
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated, user])

  useEffect(() => {
    fetchPermissions()
  }, [fetchPermissions])

  const hasPermission = useCallback(
    (documentType: string, action: PermissionAction | string): boolean => {
      // Fail-CLOSED while loading. A brief flash of hidden content is
      // preferable to a flash of gated content the user cannot actually
      // access on the server. Callers that need loading-state awareness
      // should read `loading` from the context and render a spinner.
      if (loading) return false

      // Platform users (super-admin, platform staff) always have full access.
      if (user?.kind === 'platform') return true

      // ACC-BUGS V5 (2026-09-14) — tenant admins also bypass RBAC per
      // policy (see rbac_policy_2026_08_27). Mirrors the BE
      // PermissionGuard bypass at
      // `core/rbac/permission.guard.ts:117-118`.
      if (user?.kind === 'tenant' && user?.role === 'admin') return true

      // Find the matching rule for this document type
      const rule = permissions.find((r) => r.documentType === documentType)

      // Fail-CLOSED: no rule for this doctype = deny. The BE
      // EffectivePermissionService.check now denies too, so any UI that
      // silently allowed on missing-rule would trip the server anyway.
      if (!rule) return false

      // Check the specific action in the permissions array
      const perm = rule.permissions?.find((p) => p.id === action)
      return perm ? perm.checked : false
    },
    [permissions, loading, user],
  )

  const getRestriction = useCallback(
    (documentType: string): string => {
      if (user?.kind === 'platform') return 'NONE'
      // Tenant admins also see all records (no creator restriction) —
      // matches the BE bypass in the RBAC guard.
      if (user?.kind === 'tenant' && user?.role === 'admin') return 'NONE'
      const rule = permissions.find((r) => r.documentType === documentType)
      return rule?.creatorRestriction || 'NONE'
    },
    [permissions, user],
  )

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        loading,
        hasPermission,
        getRestriction,
        refreshPermissions: fetchPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  )
}

export function usePermissionContext() {
  return useContext(PermissionContext)
}

export { PermissionContext }
